/**
 * The files "Ask Optivo" sends support with a request — screenshots first,
 * because a picture of the screen says in one look which page, which night
 * and which number the question is about.
 *
 * The server reads every file again, by its bytes
 * (`optivo-service/src/services/core/helpRequestAttachments.ts`); these rules
 * exist so the operator hears why a file cannot go while it is still in their
 * hand, rather than after Send. `help-attachments.parity.test.ts` holds the
 * bounds and the kinds to the server's.
 */

/** Mirrors `MAX_HELP_ATTACHMENTS` on the server. */
export const MAX_HELP_ATTACHMENTS = 5;

/** Mirrors `MAX_HELP_ATTACHMENT_BYTES` on the server: base64 of it fits the proxies' 10 MB. */
export const MAX_HELP_ATTACHMENT_BYTES = 7 * 1024 * 1024;

/** The kinds the server takes, by extension — a JPEG under either of its names. */
export const HELP_ATTACHMENT_EXTENSIONS = [
    "png", "jpg", "jpeg", "gif", "webp", "heic", "heif",
    "pdf",
    "mp4", "mov", "webm",
    "txt", "csv",
    "xlsx", "docx", "ods", "odt", "xls", "doc",
] as const;

/** The file picker's `accept`. */
export const HELP_ATTACHMENT_ACCEPT = HELP_ATTACHMENT_EXTENSIONS.map((extension) => `.${extension}`).join(",");

/**
 * Types that name a file the server can recognise by its bytes alone, so a
 * file whose name says nothing — a pasted screenshot — is taken on its type.
 * Text and office files are not here: the server tells those apart by name.
 */
const SELF_IDENTIFYING_TYPES = new Set([
    "image/png", "image/jpeg", "image/gif", "image/webp", "image/heic", "image/heif",
    "application/pdf",
    "video/mp4", "video/quicktime", "video/webm",
]);

const EXTENSION_OF_TYPE: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/gif": "gif",
    "image/webp": "webp",
};

export type HelpAttachmentProblem =
    | { code: "tooMany" }
    | { code: "tooLarge" }
    | { code: "type"; filename: string }
    | { code: "empty"; filename: string };

function extensionOf(name: string): string {
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

function isOffered(file: File): boolean {
    const extension = extensionOf(file.name);
    if ((HELP_ATTACHMENT_EXTENSIONS as readonly string[]).includes(extension)) return true;
    // A name that names a kind the server refuses is that kind, whatever the
    // type says: an SVG is `image/svg+xml`, a picture that carries script.
    return extension === "" && SELF_IDENTIFYING_TYPES.has(file.type);
}

function isSameFile(a: File, b: File): boolean {
    return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
}

/**
 * The files attached once `incoming` is added to `current`: every file that
 * fits, in order, and the first reason one did not. A file past the size
 * limit is left out and a smaller one after it still taken; past the count,
 * nothing more is. Adding a file already attached adds nothing.
 */
export function addHelpAttachments(
    current: readonly File[],
    incoming: readonly File[],
): { files: File[]; problem: HelpAttachmentProblem | null } {
    const files = [...current];
    let total = files.reduce((sum, file) => sum + file.size, 0);
    let problem: HelpAttachmentProblem | null = null;
    const note = (next: HelpAttachmentProblem) => {
        problem ??= next;
    };

    for (const file of incoming) {
        if (files.some((attached) => isSameFile(attached, file))) continue;
        if (file.size === 0) {
            note({ code: "empty", filename: file.name });
            continue;
        }
        if (!isOffered(file)) {
            note({ code: "type", filename: file.name });
            continue;
        }
        if (files.length >= MAX_HELP_ATTACHMENTS) {
            note({ code: "tooMany" });
            break;
        }
        if (total + file.size > MAX_HELP_ATTACHMENT_BYTES) {
            note({ code: "tooLarge" });
            continue;
        }
        files.push(file);
        total += file.size;
    }
    return { files, problem };
}

const SCREENSHOT_NAME = /^screenshot-(\d+)\./;

/**
 * A pasted picture arrives as "image.png" whatever it shows, so each one is
 * named a screenshot, numbered after those already attached; a pasted file
 * with a name of its own keeps it.
 */
export function namePastedFiles(pasted: readonly File[], current: readonly File[]): File[] {
    let next = 1 + Math.max(0, ...current.map((file) => Number(SCREENSHOT_NAME.exec(file.name)?.[1] ?? 0)));
    return pasted.map((file) => {
        const extension = EXTENSION_OF_TYPE[file.type];
        const generic = file.name === "" || /^image\.[a-z]+$/i.test(file.name);
        if (!extension || !generic) return file;
        const name = `screenshot-${next}.${extension}`;
        next += 1;
        return new File([file], name, { type: file.type, lastModified: file.lastModified });
    });
}

/** A file's size in the interface's own number format. */
export function formatAttachmentSize(bytes: number, locale: string): string {
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
    const megabytes = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / 1024 / 1024);
    return `${megabytes} MB`;
}

interface ScreenshotEnvironment {
    navigator?: { platform?: string; userAgentData?: { platform?: string } };
    matchMedia?: (query: string) => { matches: boolean };
}

/**
 * How the device in front of the operator takes a screenshot, for the hint
 * that says so. A Mac's ⌘⇧4 saves a file and shows a preview to drag, where
 * Windows' Win+Shift+S puts the picture on the clipboard to paste; a phone or
 * a tablet takes one with its buttons and attaches it from its photos. A
 * finger is read first, since an iPad says it is a Mac.
 */
export type ScreenshotPlatform = "touch" | "mac" | "other";

export function screenshotPlatform(env: ScreenshotEnvironment = globalThis): ScreenshotPlatform {
    if (env.matchMedia?.("(pointer: coarse)").matches) return "touch";
    const platform = env.navigator?.userAgentData?.platform || env.navigator?.platform || "";
    return /mac/i.test(platform) ? "mac" : "other";
}
